import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getAllReseller, getResellerMerchants } from "../../../config/LiveApi";
import AddResellerMerchantModal from "./AddResellerMerchantModal";
import ResellerMerchantEditModal from "./ResellerMerchantEditModal";

const GetReseller = () => {
  const [resellers, setResellers] = useState([]);
  const [resellerMerchants, setResellerMerchants] = useState([]);
  const [selectedReseller, setSelectedReseller] = useState(null);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState("");
  const [expandedReseller, setExpandedReseller] = useState(null);

  const token = useSelector((state) => state.userReducer.token);

  /* ---------------- Fetch Data ---------------- */

  const fetchAllData = async () => {
    try {
      const [resellerRes, merchantRes] = await Promise.all([
        getAllReseller(token),
        getResellerMerchants(token),
      ]);

      setResellers(resellerRes.data);
      setResellerMerchants(merchantRes.data);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    if (token) fetchAllData();
  }, [token]);

  /* ---------------- Merchant Mapping ---------------- */

  const merchantMap = useMemo(() => {
    const map = {};

    resellerMerchants.forEach((m) => {
      if (!map[m.resellerId]) {
        map[m.resellerId] = {
          active: [],
          inactive: [],
        };
      }

      if (m.status === 1) {
        map[m.resellerId].active.push(m);
      } else {
        map[m.resellerId].inactive.push(m);
      }
    });

    return map;
  }, [resellerMerchants]);

  /* ---------------- UI ---------------- */

  return (
    <div style={pageWrapper}>
      <div style={card}>
        <div style={header}>
          <h2 style={title}>Reseller Management</h2>
          <span style={subtitle}>Manage resellers and assign merchants</span>
        </div>

        {error && <div style={errorBox}>{error}</div>}

        <div style={tableContainer}>
          <table style={table}>
            <thead>
              <tr>
                <th style={th}></th>
                <th style={th}>Reseller Name</th>
                <th style={th}>Status</th>
                <th style={th}>Sub-Merchants</th>
                <th style={th}>Created</th>
                <th style={th}>Action</th>
              </tr>
            </thead>

            <tbody>
              {resellers.map((reseller) => {
                const merchants = resellerMerchants.filter(
                  (m) => m.resellerId === reseller.id,
                );

                const isOpen = expandedReseller === reseller.id;

                return (
                  <>
                    {/* Reseller Row */}
                    <tr key={reseller.id} style={tr}>
                      <td style={td}>
                        <span
                          style={expandBtn}
                          onClick={() =>
                            setExpandedReseller(isOpen ? null : reseller.id)
                          }
                        >
                          {isOpen ? "▾" : "▸"}
                        </span>
                      </td>

                      <td style={td}>{reseller.name || "-"}</td>

                      <td style={td}>
                        <span
                          style={{
                            ...statusBadge,
                            backgroundColor:
                              reseller.status === 1 ? "#dcfce7" : "#fee2e2",
                            color:
                              reseller.status === 1 ? "#166534" : "#991b1b",
                          }}
                        >
                          {reseller.status === 1 ? "Active" : "Inactive"}
                        </span>
                      </td>

                      <td style={td}>{merchants.length}</td>

                      <td style={td}>
                        {new Date(reseller.createdAt).toLocaleString("en-IN")}
                      </td>

                      <td style={td}>
                        <button
                          style={primaryBtn}
                          onClick={() => {
                            setSelectedReseller(reseller);
                            setShowModal(true);
                          }}
                        >
                          Add Merchant
                        </button>
                      </td>
                    </tr>

                    {/* Merchant Table */}
                    {isOpen && (
                      <tr>
                        <td colSpan="6" style={merchantContainer}>
                          <table style={subTable}>
                            <thead>
                              <tr>
                                <th style={subTh}>Sub-Merchant</th>
                                <th style={subTh}>Status</th>
                                <th style={subTh}>Payin %</th>
                                <th style={subTh}>Payout %</th>
                                <th style={subTh}>Action</th>
                              </tr>
                            </thead>

                            <tbody>
                              {merchants.length > 0 ? (
                                merchants.map((merchant) => (
                                  <tr key={merchant.id}>
                                    <td style={subTd}>{merchant.name}</td>

                                    <td style={subTd}>
                                      <span
                                        style={{
                                          ...statusBadge,
                                          backgroundColor:
                                            merchant.status === 1
                                              ? "#dcfce7"
                                              : "#fee2e2",
                                          color:
                                            merchant.status === 1
                                              ? "#166534"
                                              : "#991b1b",
                                        }}
                                      >
                                        {merchant.status === 1
                                          ? "Active"
                                          : "Inactive"}
                                      </span>
                                    </td>

                                    <td style={subTd}>
                                      {merchant.payinCommission || 0}%
                                    </td>

                                    <td style={subTd}>
                                      {merchant.payoutCommission || 0}%
                                    </td>

                                    <td style={subTd}>
                                      <button
                                        style={editBtn}
                                        onClick={() =>
                                          setSelectedMerchant(merchant)
                                        }
                                      >
                                        ✏️
                                      </button>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="5" style={emptyState}>
                                    No merchants assigned
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Merchant Modal */}
      {showModal && (
        <AddResellerMerchantModal
          reseller={selectedReseller}
          token={token}
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            setShowModal(false);
            fetchAllData();
          }}
        />
      )}

      {/* Edit Merchant Modal */}
      {selectedMerchant && (
        <ResellerMerchantEditModal
          merchant={selectedMerchant}
          token={token}
          onClose={() => setSelectedMerchant(null)}
          onSuccess={() => {
            setSelectedMerchant(null);
            fetchAllData();
          }}
        />
      )}
    </div>
  );
};

/* ---------------- Styles ---------------- */

const pageWrapper = {
  padding: "40px",
  backgroundColor: "#f9fafb",
  minHeight: "100vh",
};

const card = {
  backgroundColor: "#ffffff",
  padding: "30px",
  borderRadius: "16px",
  boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
};

const expandBtn = {
  cursor: "pointer",
  fontSize: "16px",
  fontWeight: "bold",
};

const merchantContainer = {
  background: "#f9fafb",
  padding: "15px",
};

const subTable = {
  width: "100%",
  borderCollapse: "collapse",
};

const subTh = {
  textAlign: "left",
  padding: "10px",
  fontSize: "13px",
  backgroundColor: "#f3f4f6",
};

const subTd = {
  padding: "10px",
  borderBottom: "1px solid #e5e7eb",
  fontSize: "14px",
};

const tableContainer = {
  maxHeight: "500px",
  overflowY: "auto",
  overflowX: "auto",
  borderRadius: "10px",
};

const header = { marginBottom: "25px" };

const title = {
  margin: 0,
  fontSize: "22px",
  fontWeight: "600",
};

const subtitle = {
  fontSize: "14px",
  color: "#6b7280",
};

const errorBox = {
  backgroundColor: "#fee2e2",
  color: "#991b1b",
  padding: "10px",
  borderRadius: "8px",
  marginBottom: "15px",
};

const table = {
  width: "100%",
  borderCollapse: "collapse",
};

const th = {
  textAlign: "left",
  padding: "12px",
  fontSize: "13px",
  fontWeight: "600",
  backgroundColor: "#fc8019",
  color: "#fff",
  borderBottom: "1px solid #e5e7eb",
};

const td = {
  padding: "14px 12px",
  borderBottom: "1px solid #f3f4f6",
  fontSize: "14px",
};

const tr = { transition: "background 0.2s" };

const editBtn = {
  border: "none",
  background: "#f3f4f6",
  padding: "6px 10px",
  borderRadius: "6px",
  cursor: "pointer",
  fontSize: "14px",
};

const statusBadge = {
  padding: "4px 10px",
  borderRadius: "999px",
  fontSize: "12px",
  fontWeight: "500",
};

const primaryBtn = {
  padding: "8px 14px",
  backgroundColor: "#fc8019",
  // backgroundColor: "#2563eb",
  fontWeight: "bold",
  color: "#fff",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
};

const emptyState = {
  textAlign: "center",
  padding: "30px",
  color: "#9ca3af",
};

const aliasChip = {
  display: "inline-block",
  padding: "6px 12px",
  borderRadius: "20px",
  backgroundColor: "#e0f2fe",
  color: "#0369a1",
  fontSize: "13px",
  marginRight: "6px",
  marginBottom: "6px",
  cursor: "pointer",
};

export default GetReseller;
